import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";

// Reconnect delay = base + random jitter (ms). Per-client jitter desynchronizes reconnect attempts
// across users so a recovering backend isn't hit by a synchronized reconnect storm.
const RECONNECT_BASE_MS = 5000;
const RECONNECT_JITTER_MS = 5000;

let stompClient = null;

// Job subscriptions survive reconnects: jobId → callback
const jobSubscriptions = new Map();

// Per-caller job subscriptions (trackJobProgress): key → { jobId, callback, onResync, subscription }
const trackedJobs = new Map();
let trackedJobSeq = 0;

export function subscribeToJobProgress(jobId, callback) {
  jobSubscriptions.set(jobId, callback);
  if (stompClient && stompClient.connected) {
    stompClient.subscribe(`/topic/job/${jobId}`, callback);
  }
}

export function unsubscribeFromJobProgress(jobId) {
  jobSubscriptions.delete(jobId);
}

// Never throws: held until connected, replayed (with onResync) on every reconnect. Returns a disposer.
export function trackJobProgress(jobId, callback, onResync) {
  const key = ++trackedJobSeq;
  const entry = { jobId, callback, onResync, subscription: null };
  trackedJobs.set(key, entry);
  if (stompClient && stompClient.connected) {
    try {
      entry.subscription = stompClient.subscribe(`/topic/job/${jobId}`, callback);
    } catch (err) {
      // Status reads cover the job until the next connect replays this subscription.
      console.warn("Job subscribe failed; relying on status reads", jobId, err);
    }
  }
  return () => {
    trackedJobs.delete(key);
    if (entry.subscription && stompClient && stompClient.connected) {
      try {
        entry.subscription.unsubscribe();
      } catch (_) {}
    }
    entry.subscription = null;
  };
}

// Plan processing-status subscriptions survive reconnects: planId → { callback, subscription }.
// The backend pushes IN_PROGRESS / COMPLETED / FAILED to /topic/plan-status/{planId} while a
// copy-from-plan / copy-from-actuals / actualization job runs on that plan.
const planStatusSubscriptions = new Map();

export function subscribeToPlanStatus(planId, callback) {
  unsubscribeFromPlanStatus(planId);
  const entry = { callback, subscription: null };
  planStatusSubscriptions.set(String(planId), entry);
  if (stompClient && stompClient.connected) {
    entry.subscription = stompClient.subscribe(`/topic/plan-status/${planId}`, callback);
  }
}

export function unsubscribeFromPlanStatus(planId) {
  const entry = planStatusSubscriptions.get(String(planId));
  if (entry?.subscription) {
    try {
      entry.subscription.unsubscribe();
    } catch (_) {}
  }
  planStatusSubscriptions.delete(String(planId));
}

export function createStompClient(url, token, options = {}) {
  if (stompClient) {
    try {
      stompClient.deactivate();
    } catch (_) {}
    stompClient = null;
  }

  stompClient = new Client({
    webSocketFactory: () => new SockJS(url),
    reconnectDelay: RECONNECT_BASE_MS + Math.floor(Math.random() * RECONNECT_JITTER_MS),
    heartbeatIncoming: 4000,
    heartbeatOutgoing: 4000,
    connectHeaders: token ? {
      'Authorization': `Bearer ${token}`
    } : {},
    onConnect: () => {
      console.info("STOMP connected");
      // Re-subscribe all active job progress subscriptions after reconnect
      jobSubscriptions.forEach((callback, jobId) => {
        stompClient.subscribe(`/topic/job/${jobId}`, callback);
      });
      // Re-subscribe all active plan-status subscriptions after reconnect
      planStatusSubscriptions.forEach((entry, planId) => {
        entry.subscription = stompClient.subscribe(`/topic/plan-status/${planId}`, entry.callback);
      });
      trackedJobs.forEach((entry) => {
        try {
          entry.subscription = stompClient.subscribe(`/topic/job/${entry.jobId}`, entry.callback);
        } catch (err) {
          console.warn("Job re-subscribe failed; relying on status reads", entry.jobId, err);
        }
        if (entry.onResync) entry.onResync();
      });
      if (options.onConnect) {
        options.onConnect(stompClient);
      }
    },
    onDisconnect: () => {
      console.info("STOMP disconnected");
      if (options.onDisconnect) {
        options.onDisconnect();
      }
    },
    onStompError: (frame) => {
      console.error("Broker error:", frame.headers.message);
      if (options.onStompError) {
        options.onStompError(frame);
      }
    },
    onWebSocketError: (event) => {
      console.error("WebSocket error:", event);
      if (options.onWebSocketError) {
        options.onWebSocketError(event);
      }
    },
    debug: () => {},
  });

  stompClient.activate();
  return stompClient;
}

export function disconnectStomp() {
  if (stompClient) {
    stompClient.deactivate();
    stompClient = null;
  }
}

export function getStompClient() {
  return stompClient;
}
